import React, { Component } from "react";
import client from "@culturehq/client";
import { Button, Panel, PlainButton, Thumbnail, Tooltip, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import { t, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import AttendeesModal from "components/event/modals/AttendeesModal";
import InviteModal from "components/event/modals/InviteModal";
import getUserName from "app/utils/getUserName";

const TABS = [
  { label: t("Yes"), value: "accepted" },
  { label: t("Interested"), value: "interested" },
  { label: t("Invited"), value: "invited" }
];

const AttendeePreview = ({ rsvp }) => {
  const { user } = rsvp;

  return (
    <div className="col-xs-6 attendee">
      <Thumbnail className="Thumbnail" image={user.avatar.thumbUrl} title={getUserName(user)} />
      <div className="attendee-name">
        <ProfileLink user={rsvp.user} />
      </div>
    </div>
  );
};

const AttendeePreviewTab = ({ onClick, selected, event, tab: { value, label } }) => {
  const count = event && event.rsvpBreakdown && event.rsvpBreakdown[value];
  const isActive = selected === value;
  const activeClass = isActive ? "active" : "inactive";

  return (
    <li role="presentation" key={value} onClick={() => onClick(value)}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a className={activeClass}>
        {label} {count > 0 && ` (${count})`}
      </a>
    </li>
  );
};

const getNoInviteMessage = (event, user) => {
  if (event.isPast()) {
    return "You cannot invite colleagues to a past event.";
  }

  if (event.isUserHostOrAdmin(user)) {
    return null;
  }

  if (!event.openInvites) {
    return "This event does not allow inviting other colleagues.";
  }

  if ((event.rsvpForUser || {}).responseType !== "accepted") {
    return "You must be RSVPd \"Yes\" to invite colleagues.";
  }

  return null;
};

const InviteModalTrigger = ({ event, onRefreshInvitations, profile }) => {
  const noInviteMessage = getNoInviteMessage(event, profile);

  if (noInviteMessage) {
    return (
      <Tooltip tip={noInviteMessage}>
        <Button icon="person-add" fillParent disabled>
          <Trans>Invite Users</Trans>
        </Button>
      </Tooltip>
    );
  }

  return (
    <InviteModal event={event} onRefreshInvitations={onRefreshInvitations}>
      {onTrigger => (
        <Button icon="person-add" fillParent onClick={onTrigger}>
          <Trans>Invite Users</Trans>
        </Button>
      )}
    </InviteModal>
  );
};

class EventAttendeesContext extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      responseType: "accepted",
      rsvps: { accepted: props.event.rsvpPreview }
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { event } = this.props;
    const { responseType, rsvps } = this.state;

    if (responseType !== prevState.responseType
      || event.rsvpBreakdown[responseType] !== rsvps[responseType].length) {
      this.fetchRsvps();
    }
  }

  handleFilterChange = responseType => {
    this.setState(({ rsvps }) => ({
      loading: !(responseType in rsvps),
      responseType
    }));
  };

  handleRefreshInvitations = async () => {
    const { onRefreshInvitations } = this.props;

    await this.fetchRsvps();
    onRefreshInvitations();
  };

  async fetchRsvps() {
    const { event: { id: eventId } } = this.props;
    const { responseType } = this.state;

    const { rsvps: fetched } = await client.listEventRsvps({ eventId, responseType });

    this.setState(({ rsvps }) => ({
      loading: false,
      rsvps: { ...rsvps, [responseType]: fetched.slice(0, 8) }
    }));
  }

  render() {
    const { event, onEventUpdate, profile } = this.props;
    const { loading, responseType, rsvps } = this.state;

    const visibleRsvps = rsvps[responseType] || [];

    return (
      <Panel className="EventView__AttendeesPanel">
        <ul className="nav nav-tabs nav-tabs-green">
          {TABS.map(tab => (
            <AttendeePreviewTab
              key={tab.value}
              event={event}
              selected={responseType}
              loading={loading}
              tab={tab}
              onClick={this.handleFilterChange}
            />
          ))}
        </ul>
        <Panel.Body>
          <ErrorBoundary>
            <div className="clearfix">
              {visibleRsvps.map(rsvp => (
                <AttendeePreview key={rsvp.id} rsvp={rsvp} />
              ))}
            </div>

            {!loading && visibleRsvps.length < 1 && (
              <Warning><Trans>No attendees to show.</Trans></Warning>
            )}

            <div className="clearfix attendees-action-buttons">
              <div className="col-xs-12 col-lg-6">
                <AttendeesModal
                  event={event}
                  onEventUpdate={onEventUpdate}
                  onRefreshInvitations={this.handleRefreshInvitations}
                >
                  {onTrigger => (
                    <PlainButton className="link action" onClick={onTrigger}>
                      <Trans>View all guests</Trans>
                    </PlainButton>
                  )}
                </AttendeesModal>
              </div>
              <div className="col-xs-12 col-lg-6">
                <InviteModalTrigger
                  event={event}
                  onRefreshInvitations={this.handleRefreshInvitations}
                  profile={profile}
                />
              </div>
            </div>
          </ErrorBoundary>
        </Panel.Body>
      </Panel>
    );
  }
}

const EventAttendees = ({ event, onEventUpdate, onRefreshInvitations }) => {
  const { profile } = useProfile();

  return (
    <EventAttendeesContext
      event={event}
      onEventUpdate={onEventUpdate}
      onRefreshInvitations={onRefreshInvitations}
      profile={profile}
    />
  );
};

export default EventAttendees;
